import { useMemo, useState, useEffect } from 'react'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Form } from 'semantic-ui-react'
import { FormDateField, FormTextFormField, FormDropdownField } from 'elements'
import { FloppyDisk, Plus, Trash } from 'phosphor-react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import InvoiceEmailModal from './invoiceEmailModal'
import { GetInvoiceById } from 'hooks/invoice'
import { useInvoiceSettings, useNotification, useCurrenciesOptions } from 'hooks'
import { AuthService, InvoiceService, DatePickerTypes, DateUtility } from 'utility'
import { DesktopMode, MobileMode } from 'layout'

// ---------------------------------------------
// Yup Validation Schema aligned to invoice schema
// ---------------------------------------------
const InvoiceSchema = yup.object().shape({
    CustomerName: yup.string().required('Customer name is required'),
    CustomerEmail: yup
        .string()
        .email('Invalid email')
        .required('Customer email is required'),
    BillingAddress: yup.string().nullable(),

    InvoiceDate: yup.date().required('Invoice date is required'),
    DueDate: yup.date().required('Due date is required'),

    CurrencyCode: yup.string().required('Currency is required'),
    TaxRatePercent: yup.number().min(0, 'Tax cannot be negative').nullable(),
    ShippingAmount: yup
        .number()
        .transform(v => (Number.isNaN(v) ? 0 : v))
        .min(0, 'Shipping cannot be negative')
        .nullable(),
    PaymentTermsDays: yup
        .number()
        .transform(v => (Number.isNaN(v) ? undefined : v))
        .nullable(),

    Lines: yup
        .array()
        .of(
            yup.object().shape({
                InvoiceLineId: yup.number().nullable(),
                LineNumber: yup.number().nullable(),
                LineType: yup.number().nullable(),
                Description: yup.string().required('Description is required'),
                Quantity: yup
                    .number()
                    .positive('Quantity must be > 0')
                    .required(),
                UnitPrice: yup
                    .number()
                    .min(0, 'Unit price cannot be negative')
                    .required(),
                DiscountAmount: yup
                    .number()
                    .transform(v => (Number.isNaN(v) ? 0 : v))
                    .min(0, 'Discount cannot be negative')
                    .nullable(),
                TaxRatePercent: yup
                    .number()
                    .transform(v => (Number.isNaN(v) ? undefined : v))
                    .nullable(),
            }),
        )
        .min(1, 'At least one line item is required'),
    NotesPublic: yup.string().nullable(),
    NotesInternal: yup.string().nullable(),
    TermsAndConditions: yup.string().nullable(),
})

// -----------------------------------------------------
// Component
// -----------------------------------------------------
export const InvoiceEditor = ({ onSave }) => {
    const { id } = useParams()
    const location = useLocation()
    const navState = location?.state || {}
    const isCreateMode = id === 'new' || navState?.mode === 'create'
    const [openEmailModal, setOpenEmailModal] = useState(false)
    const [sendingEmail, setSendingEmail] = useState(false)
    const { data: currencyOptions } = useCurrenciesOptions()
    const navigate = useNavigate()
    const { successToast, errorToast } = useNotification()

    const { data } = GetInvoiceById(isCreateMode ? null : id, {
        enabled: !isCreateMode,
    })

    const readOnly =
        navState?.mode === 'view' || (!!data && data.InvoiceStatus === 5)

    const {
        control,
        handleSubmit,
        watch,
        getValues,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(InvoiceSchema),
        defaultValues: {
            CustomerName: '',
            CustomerEmail: '',
            BillingAddress: '',
            InvoiceNumberDisplay: '',
            InvoiceDate: new Date(),
            DueDate: new Date(),
            CurrencyCode: '',
            TaxRatePercent: 0,
            ShippingAmount: 0,
            PaymentTermsDays: 0,
            Lines: [
                {
                    Description: '',
                    Quantity: 1,
                    UnitPrice: 0,
                    DiscountAmount: 0,
                    TaxRatePercent: 0,
                },
            ],
            NotesPublic: '',
            NotesInternal: '',
            TermsAndConditions: '',
        },
    })

    const { data: invoiceSetting } = useInvoiceSettings({
        enabled: isCreateMode, // Only fetch settings for new invoices
    })

    // Load existing invoice data
    useEffect(() => {
        if (!isCreateMode && data) {
            const defaultValues = {
                CustomerName: data.CustomerName || '',
                CustomerEmail: data.CustomerEmail || '',
                BillingAddress: data.BillingAddress || '',
                InvoiceNumberDisplay:
                    data.InvoiceNumberDisplay || data.InvoiceNumber || '',
                InvoiceDate: data.InvoiceDate
                    ? DateUtility.parseApiDate(data.InvoiceDate)
                    : new Date(),
                DueDate: data.DueDate
                    ? DateUtility.parseApiDate(data.DueDate)
                    : new Date(),
                CurrencyCode: data.CurrencyCode || 'INR',
                TaxRatePercent: Number(data.TaxRatePercent ?? 0),
                ShippingAmount: Number(data.ShippingAmount ?? 0),
                PaymentTermsDays: Number(data.PaymentTermsDays ?? 0),
                Lines: (data.Lines || []).map((l, idx) => ({
                    InvoiceLineId: l.InvoiceLineId ?? null,
                    LineNumber: l.LineNumber ?? idx + 1,
                    LineType: l.LineType ?? 1,
                    Description: l.Description || '',
                    Quantity: Number(l.Quantity ?? 1),
                    UnitPrice: Number(l.UnitPrice ?? 0),
                    DiscountAmount: Number(l.DiscountAmount ?? 0),
                    TaxRatePercent:
                        l.TaxRatePercent !== undefined &&
                            l.TaxRatePercent !== null
                            ? Number(l.TaxRatePercent)
                            : undefined,
                    AuditId: l.AuditId ?? null,
                    TemplateId: l.TemplateId ?? null,
                })),
                NotesPublic: data.NotesPublic || '',
                NotesInternal: data.NotesInternal || '',
                TermsAndConditions: data.TermsAndConditions || '',
            }

            reset(defaultValues)
        }
    }, [data, isCreateMode, reset])

    // Initialize new invoice with defaults and navigation state
    useEffect(() => {
        if (!isCreateMode) return

        const selected = Array.isArray(navState?.SelectedAudits)
            ? navState.SelectedAudits
            : []
        const hasSelection = selected.length > 0

        // Create default lines from selection or navigation state
        const defaultLines = hasSelection
            ? selected.map((a, idx) => ({
                Description: a?.AuditName || `Audit ${idx + 1}`,
                Quantity: 1,
                UnitPrice: 0,
                DiscountAmount: 0,
                TaxRatePercent: 0,
                AuditId: a?.AuditId || a?.TaskId || null,
                TemplateId: a?.TemplateId || navState?.TemplateId || null,
            }))
            : [
                {
                    Description: navState?.AuditName || 'Audit Service',
                    Quantity: 1,
                    UnitPrice: 0,
                    DiscountAmount: 0,
                    TaxRatePercent: 0,
                    AuditId: navState?.TaskId || null,
                    TemplateId: navState?.TemplateId || null,
                },
            ]

        // Get customer info from navigation state or first selected audit
        const defaultCustomerName =
            navState?.CustomerName || selected?.[0]?.CustomerName || ''
        const defaultCustomerEmail =
            navState?.EmailAddress || selected?.[0]?.EmailAddress || ''

        // Set initial form values
        const initialValues = {
            CustomerName: defaultCustomerName,
            CustomerEmail: defaultCustomerEmail,
            BillingAddress: '',
            InvoiceNumberDisplay: '',
            InvoiceDate: new Date(),
            DueDate: new Date(),
            CurrencyCode: '',
            TaxRatePercent: 0,
            ShippingAmount: 0,
            PaymentTermsDays: 0,
            Lines: defaultLines,
            NotesPublic: '',
            NotesInternal: '',
            TermsAndConditions: '',
        }

        reset(initialValues)
    }, [isCreateMode, navState, reset])

    // Apply invoice settings when they're loaded for new invoices
    useEffect(() => {
        if (!isCreateMode || !invoiceSetting) return

        const currentValues = getValues()
        const invoiceDate = currentValues.InvoiceDate
            ? new Date(currentValues.InvoiceDate)
            : new Date()

        // Calculate due date based on payment terms
        const paymentTerms = Number(
            invoiceSetting.DefaultPaymentTermsDays !== undefined &&
                invoiceSetting.DefaultPaymentTermsDays !== null
                ? invoiceSetting.DefaultPaymentTermsDays
                : 0,
        )

        const dueDate = new Date(invoiceDate)
        if (!Number.isNaN(paymentTerms) && paymentTerms > 0) {
            dueDate.setDate(dueDate.getDate() + paymentTerms)
        }

        // Only update if not already set
        const updates = {}
        if (!currentValues.CurrencyCode && invoiceSetting.DefaultCurrencyCode) {
            updates.CurrencyCode = invoiceSetting.DefaultCurrencyCode
        }
        if (paymentTerms > 0) {
            updates.PaymentTermsDays = paymentTerms
            updates.DueDate = dueDate
        }
        if (
            invoiceSetting.DefaultTaxRatePercent !== undefined &&
            invoiceSetting.DefaultTaxRatePercent !== null
        ) {
            updates.TaxRatePercent = Number(
                invoiceSetting.DefaultTaxRatePercent,
            )

            // Apply default tax rate to all lines that don't have one set
            if (currentValues.Lines?.length) {
                updates.Lines = currentValues.Lines.map(line => ({
                    ...line,
                    TaxRatePercent:
                        line.TaxRatePercent ||
                        Number(invoiceSetting.DefaultTaxRatePercent),
                }))
            }
        }

        if (Object.keys(updates).length > 0) {
            reset(
                {
                    ...currentValues,
                    ...updates,
                },
                {
                    keepDirty: true, // Keep user's input if they've already started typing
                    keepErrors: true, // Keep any validation errors
                },
            )
        }
    }, [invoiceSetting, isCreateMode, getValues, reset])

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'Lines',
    })

    // instead of watch('lineItems')
    const lineItems = useWatch({
        control,
        name: 'Lines',
    })

    // taxRate also from useWatch (optional but recommended)
    const shippingAmount = Number(
        useWatch({
            control,
            name: 'ShippingAmount',
        }) || 0,
    )

    const { subtotal, discountTotal, taxAmount, total } = useMemo(() => {
        const subtotalValue = (lineItems ?? []).reduce((sum, item = {}) => {
            const { Quantity = 0, UnitPrice = 0 } = item

            const quantity = Number(Quantity)
            const price = Number(UnitPrice)

            return sum + quantity * price
        }, 0)

        const discountValue = (lineItems ?? []).reduce((sum, item = {}) => {
            const { DiscountAmount = 0 } = item
            const disc = Number(DiscountAmount)

            return sum + disc
        }, 0)

        const taxValue = (lineItems ?? []).reduce((sum, item = {}) => {
            const {
                Quantity = 0,
                UnitPrice = 0,
                DiscountAmount = 0,
                TaxRatePercent = 0,
            } = item

            const quantity = Number(Quantity)
            const price = Number(UnitPrice)
            const disc = Number(DiscountAmount)
            const lineTaxRate = Number(TaxRatePercent)

            const taxable = Math.max(0, quantity * price - disc)

            return sum + (taxable * lineTaxRate) / 100
        }, 0)

        const totalBeforeShip =
            Math.max(0, subtotalValue - discountValue) + taxValue
        const grandTotal = totalBeforeShip + (shippingAmount || 0)

        return {
            subtotal: subtotalValue,
            discountTotal: discountValue,
            taxAmount: taxValue,
            totalBeforeShipping: totalBeforeShip,
            total: grandTotal,
        }
    }, [lineItems, shippingAmount])

    const formatTermsAndConditions = () => {
        if (!invoiceSetting?.TermsAndConditions?.length) {
            return ''
        }
        return invoiceSetting.TermsAndConditions.map(item => {
            const title = item?.TermTitle ? `${item.TermTitle}:` : ''
            const text = item?.TermText || ''
            return `${title} ${text}`.trim()
        })
            .filter(Boolean)
            .join('\n')
    }

    const onSubmit = async (formData, desiredStatus) => {
        // Build common header values from form
        const companyId = AuthService.getUser()?.Companies?.[0]?.CompanyId
        const headerBase = {
            CompanyId: data?.CompanyId ?? companyId,
            CustomerId:
                formData.CustomerId ??
                data?.CustomerId ??
                navState?.CustomerId ??
                0,
            CustomerName: formData.CustomerName,
            CustomerEmail: formData.CustomerEmail,
            BillingAddress: formData.BillingAddress || null,
            InvoiceNumber: data?.InvoiceNumber,
            InvoiceNumberInt: data?.InvoiceNumberInt,
            InvoiceNumberDisplay:
                formData.InvoiceNumberDisplay || data?.InvoiceNumberDisplay,
            InvoiceType: data?.InvoiceType ?? 1,
            InvoiceStatus: desiredStatus ?? data?.InvoiceStatus ?? 0,
            SourceType: data?.SourceType ?? 0,
            InvoiceDate: DateUtility.formatApiDate(formData.InvoiceDate),
            DueDate: DateUtility.formatApiDate(formData.DueDate),
            CurrencyCode: formData.CurrencyCode,
            TaxRatePercent: Number(formData.TaxRatePercent ?? 0),
            SubtotalAmount: subtotal,
            DiscountAmount: discountTotal,
            TaxAmount: taxAmount,
            ShippingAmount: Number(formData.ShippingAmount || 0),
            TotalAmount: total,
            AmountPaid: data?.AmountPaid ?? 0,
            AmountOutstanding: Math.max(0, total - (data?.AmountPaid ?? 0)),
            PaymentTermsDays: Number(formData.PaymentTermsDays ?? 0),
            ReferenceNumber: formData.ReferenceNumber ?? null,
            NotesInternal: formData.NotesInternal || '',
            NotesPublic: formData.NotesPublic || '',
            TermsAndConditions: formatTermsAndConditions(),
            IsDeleted: false,
        }

        // Map lines
        const linesPayload = (formData.Lines || []).map((l, idx) => {
            const quantity = Number(l.Quantity || 0)
            const unitPrice = Number(l.UnitPrice || 0)
            const discount = Number(l.DiscountAmount || 0)
            const rate =
                l.TaxRatePercent !== undefined && l.TaxRatePercent !== null
                    ? Number(l.TaxRatePercent)
                    : 0
            const taxable = Math.max(0, quantity * unitPrice - discount)
            const lineTaxAmount = (taxable * rate) / 100
            return {
                InvoiceLineId: l.InvoiceLineId ?? 0,
                InvoiceId: data?.InvoiceId,
                LineNumber: l.LineNumber ?? idx + 1,
                LineType: l.LineType ?? 1,
                Description: l.Description,
                Quantity: quantity,
                UnitPrice: unitPrice,
                DiscountAmount: discount,
                TaxAmount: lineTaxAmount,
                TaxRatePercent: rate,
                AuditId: l.AuditId ?? null,
                TemplateId: l.TemplateId ?? navState?.TemplateId ?? null,
                LocationId: l.LocationId ?? null,
                TaxRateId: l.TaxRateId ?? null,
            }
        })

        try {
            if (isCreateMode) {
                // Build arrays for LinkedAuditIds and AuditAllocations from line AuditIds
                const auditIds = Array.from(
                    new Set(
                        (formData.Lines || [])
                            .map(l =>
                                l.AuditId !== undefined && l.AuditId !== null
                                    ? Number(l.AuditId)
                                    : 0,
                            )
                            .filter(id => id > 0),
                    ),
                )

                // Split total equally among audits if present
                const allocations = auditIds.length
                    ? auditIds.map(id => ({
                        AuditId: id,
                        AmountAllocated: Number(total / auditIds.length || 0),
                    }))
                    : []

                // Helpers to normalize values (no nulls)
                const str = v =>
                    v === undefined || v === null ? '' : String(v)
                const num = v => {
                    const n = Number(v)
                    return Number.isFinite(n) ? n : 0
                }

                // Create invoice per API contract: wrap header in InvoiceHeader
                const createPayload = {
                    InvoiceHeader: {
                        CompanyId: num(headerBase.CompanyId),
                        CustomerId: num(headerBase.CustomerId),
                        CustomerName: str(headerBase.CustomerName),
                        CustomerEmail: str(headerBase.CustomerEmail),
                        BillingAddress: str(headerBase.BillingAddress),
                        InvoiceType: num(headerBase.InvoiceType),
                        InvoiceStatus:
                            desiredStatus ?? num(headerBase.InvoiceStatus),
                        SourceType: num(headerBase.SourceType),
                        InvoiceDate: str(headerBase.InvoiceDate),
                        DueDate: str(headerBase.DueDate),
                        CurrencyCode: str(headerBase.CurrencyCode),
                        TaxRatePercent: num(headerBase.TaxRatePercent),
                        SubtotalAmount: num(headerBase.SubtotalAmount),
                        DiscountAmount: num(headerBase.DiscountAmount),
                        TaxAmount: num(headerBase.TaxAmount),
                        ShippingAmount: num(headerBase.ShippingAmount),
                        TotalAmount: num(headerBase.TotalAmount),
                        AmountPaid: num(headerBase.AmountPaid),
                        PaymentTermsDays: num(headerBase.PaymentTermsDays),
                        ReferenceNumber: str(headerBase.ReferenceNumber),
                        NotesInternal: str(headerBase.NotesInternal),
                        NotesPublic: str(headerBase.NotesPublic),
                        TermsAndConditions: formatTermsAndConditions(),
                        LastEmailSentOn: '',
                        LastEmailSentTo: '',
                        Lines: (formData.Lines || []).map((l, idx) => {
                            const quantity = num(l.Quantity)
                            const unitPrice = num(l.UnitPrice)
                            const discount = num(l.DiscountAmount)
                            const rate =
                                l.TaxRatePercent !== undefined &&
                                    l.TaxRatePercent !== null
                                    ? num(l.TaxRatePercent)
                                    : 0
                            const taxable = Math.max(
                                0,
                                quantity * unitPrice - discount,
                            )
                            const lineTaxAmount = (taxable * rate) / 100
                            return {
                                InvoiceLineId: 0,
                                InvoiceId: 0,
                                LineNumber: num(l.LineNumber ?? idx + 1),
                                LineType: num(l.LineType ?? 1),
                                Description: str(l.Description),
                                Quantity: quantity,
                                UnitPrice: unitPrice,
                                DiscountAmount: discount,
                                TaxAmount: num(lineTaxAmount),
                                AuditId: num(l.AuditId ?? 0),
                                TemplateId: num(
                                    l.TemplateId ?? navState?.TemplateId ?? 0,
                                ),
                                LocationId: num(l.LocationId ?? 0),
                                TaxRateId: num(l.TaxRateId ?? 0),
                                TaxRatePercent: rate,
                            }
                        }),
                    },
                    LinkedAuditIds: auditIds,
                    AuditAllocations: allocations,
                }
                const res = await InvoiceService.create(createPayload)
                const newInvoiceId =
                    res?.InvoiceId ?? res?.Data?.InvoiceId ?? res?.id ?? res
                if (!newInvoiceId) throw new Error('Failed to create invoice')
                successToast('Invoice created')
                // Go back to previous screen (preserves prior filters)
                navigate(-1)
                return
            }

            // Update existing invoice with a single API call
            const headerPayload = {
                ...headerBase,
                InvoiceId: data?.InvoiceId || 0,
                CompanyId: data?.CompanyId || 0,
                CustomerId: data?.CustomerId || 0,
                CustomerName: headerBase.CustomerName || '',
                CustomerEmail: headerBase.CustomerEmail || '',
                BillingAddress: headerBase.BillingAddress || '',
                InvoiceNumber: data?.InvoiceNumber || '',
                InvoiceNumberInt: data?.InvoiceNumberInt || 0,
                InvoiceNumberDisplay: headerBase.InvoiceNumberDisplay || '',
                InvoiceType: data?.InvoiceType || 0,
                InvoiceStatus: desiredStatus ?? data?.InvoiceStatus ?? 0,
                SourceType: data?.SourceType || 0,
                InvoiceDate: headerBase.InvoiceDate || '',
                DueDate: headerBase.DueDate || '',
                CurrencyCode: headerBase.CurrencyCode || '',
                TaxRatePercent: Number(headerBase.TaxRatePercent || 0),
                SubtotalAmount: Number(headerBase.SubtotalAmount || 0),
                DiscountAmount: Number(headerBase.DiscountAmount || 0),
                TaxAmount: Number(headerBase.TaxAmount || 0),
                ShippingAmount: Number(headerBase.ShippingAmount || 0),
                TotalAmount: Number(headerBase.TotalAmount || 0),
                AmountPaid: Number(headerBase.AmountPaid || 0),
                AmountOutstanding: Number(headerBase.AmountOutstanding || 0),
                PaymentTermsDays: Number(headerBase.PaymentTermsDays || 0),
                ReferenceNumber: headerBase.ReferenceNumber || '',
                NotesInternal: headerBase.NotesInternal || '',
                NotesPublic: headerBase.NotesPublic || '',
                TermsAndConditions: formatTermsAndConditions() || '',
                LastEmailSentOn: data?.LastEmailSentOn || '',
                LastEmailSentTo: data?.LastEmailSentTo || '',
                IsDeleted: false,
                CreatedByUserId: data?.CreatedByUserId || 0,
                CreatedOn: data?.CreatedOn || new Date().toISOString(),
                ModifiedByUserId: AuthService.getUser()?.UserId || 0,
                ModifiedOn: new Date().toISOString(),
                CompanyName: data?.CompanyName || '',
                AuditLinks: data?.AuditLinks || [],
                LinkedAuditIds: data?.LinkedAuditIds || [],
                AuditReferences: data?.AuditReferences || [],
            }

            // Update both header and lines in a single API call
            await InvoiceService.updateHeader(headerPayload, linesPayload)

            if (onSave) onSave({ header: headerPayload, lines: linesPayload })
            // No need to refetch when navigating back
            successToast('Invoice saved')
            navigate(-1)
        } catch (e) {
            console.error('Failed to save invoice', e)
            errorToast('Failed to save invoice')
        }
    }

    const canSend = false

    const handleSave = () => {
        handleSubmit(onSubmit, 0)()
    }

    const handleSubmitInvoiceEmail = async toEmail => {
        if (!data?.InvoiceId) return
        try {
            setSendingEmail(true)
            const companyId =
                data?.CompanyId ??
                AuthService.getUser()?.Companies?.[0]?.CompanyId
            const invNo =
                data?.InvoiceNumberDisplay || data?.InvoiceNumber || ''
            const payload = {
                InvoiceId: data.InvoiceId,
                CompanyId: companyId,
                ToEmail: toEmail,
                Cc: '',
                Subject: invNo
                    ? `Invoice ${invNo} from eAuditor`
                    : 'Invoice from eAuditor',
                Body: 'Dear Customer,\n\nPlease find your attached invoice.\n\nThank you.',
                AttachPdf: true,
                PdfFilePath: '',
            }
            await InvoiceService.sendEmail(payload)
            successToast('Invoice email sent')
            setOpenEmailModal(false)
        } catch (e) {
            console.error('Failed to send invoice email', e)
            errorToast('Failed to send invoice email')
        } finally {
            setSendingEmail(false)
        }
    }

    return (
        <>
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-sm">
                        <div className="p-8">
                            {/* HEADER */}
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900">
                                        Invoice
                                    </h1>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {watch('InvoiceNumberDisplay') || ''}
                                    </p>
                                </div>

                                <div className="flex gap-3">
                                    {canSend && (
                                        <button
                                            type="button"
                                            onClick={handleSave}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* ALERTS */}
                            {/* {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-800">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-800">
                {successMessage}
              </div>
            )} */}

                            <Form noValidate>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="text-lg font-semibold">
                                            Customer Details
                                        </h3>
                                        <FormTextFormField
                                            control={control}
                                            name="CustomerName"
                                            label="Customer Name *"
                                            errors={errors.CustomerName}
                                            disabled={readOnly}
                                        />

                                        <FormTextFormField
                                            control={control}
                                            name="CustomerEmail"
                                            label="Email *"
                                            errors={errors.CustomerEmail}
                                            disabled={readOnly}
                                        />

                                        <FormTextFormField
                                            control={control}
                                            name="BillingAddress"
                                            label="Billing Address"
                                            errors={errors.BillingAddress}
                                            multiline
                                            disabled={readOnly}
                                        />
                                    </div>

                                    <div>
                                        <h3 className="text-lg font-semibold">
                                            Invoice Details
                                        </h3>
                                        <FormTextFormField
                                            control={control}
                                            name="InvoiceNumberDisplay"
                                            label="Invoice #"
                                            errors={errors.InvoiceNumberDisplay}
                                            disabled
                                        />

                                        <FormDateField
                                            name="InvoiceDate"
                                            control={control}
                                            label="Invoice Date *"
                                            errors={errors.InvoiceDate}
                                            type={DatePickerTypes.Single}
                                            disabled={readOnly}
                                        />

                                        <FormDateField
                                            name="DueDate"
                                            control={control}
                                            label="Due Date *"
                                            errors={errors.DueDate}
                                            type={DatePickerTypes.Single}
                                            disabled={readOnly}
                                        />

                                        <FormDropdownField
                                            control={control}
                                            name="CurrencyCode"
                                            label="Currency Code *"
                                            placeholder="Select Currency"
                                            options={currencyOptions}
                                            errors={errors.CurrencyCode}
                                            disabled={readOnly}
                                        />

                                        <FormTextFormField
                                            control={control}
                                            name="PaymentTermsDays"
                                            label="Payment Terms (Days)"
                                            type="number"
                                            errors={errors.PaymentTermsDays}
                                            disabled={readOnly}
                                        />
                                    </div>
                                </div>

                                {/* -------------------- LINE ITEMS -------------------- */}
                                <div className="mt-6">
                                    <h2 className="text-lg font-semibold">
                                        Line Items
                                    </h2>

                                    {errors.Lines && (
                                        <p className="text-red-500">
                                            {errors.Lines.message}
                                        </p>
                                    )}

                                    {/* Desktop View: Table */}
                                    <DesktopMode>
                                        <table className="w-full mt-3 border rounded-lg overflow-hidden">
                                            <thead>
                                                <tr>
                                                    <th className="w-2/5 pr-2 py-3 text-left">
                                                        Description
                                                    </th>
                                                    <th className="w-1/6 pr-2 py-3 text-center">
                                                        Qty
                                                    </th>
                                                    <th className="w-1/6 pr-2 py-3 text-center">
                                                        Unit Price
                                                    </th>
                                                    <th className="w-1/6 pr-2 py-3 text-center">
                                                        Discount
                                                    </th>
                                                    <th className="w-1/6 pr-2 py-3 text-center">
                                                        Tax %
                                                    </th>
                                                    <th className="w-1/6 pr-2 py-3 text-right">
                                                        Amount
                                                    </th>
                                                    <th className="w-1/6 py-3 text-center">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {fields.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td className="pr-2 py-3">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.Description`}
                                                                placeholder="Description"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]?.Description
                                                                }
                                                            />
                                                        </td>

                                                        <td className="pr-2 py-3">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.Quantity`}
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]?.Quantity
                                                                }
                                                            />
                                                        </td>

                                                        <td className="pr-2 py-3">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.UnitPrice`}
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]?.UnitPrice
                                                                }
                                                            />
                                                        </td>

                                                        <td className="pr-2 py-3">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.DiscountAmount`}
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]
                                                                        ?.DiscountAmount
                                                                }
                                                            />
                                                        </td>

                                                        <td className="pr-2 py-3">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.TaxRatePercent`}
                                                                type="number"
                                                                placeholder=""
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]
                                                                        ?.TaxRatePercent
                                                                }
                                                            />
                                                        </td>

                                                        <td className="pr-2 py-3 text-right font-semibold text-gray-700">
                                                            {(() => {
                                                                const quantity =
                                                                    Number(
                                                                        lineItems?.[
                                                                            index
                                                                        ]
                                                                            ?.Quantity ||
                                                                        0,
                                                                    )
                                                                const unitPrice =
                                                                    Number(
                                                                        lineItems?.[
                                                                            index
                                                                        ]
                                                                            ?.UnitPrice ||
                                                                        0,
                                                                    )
                                                                const disc = Number(
                                                                    lineItems?.[
                                                                        index
                                                                    ]
                                                                        ?.DiscountAmount ||
                                                                    0,
                                                                )
                                                                const lineTaxRate =
                                                                    lineItems?.[
                                                                        index
                                                                    ]
                                                                        ?.TaxRatePercent !==
                                                                        undefined &&
                                                                        lineItems?.[
                                                                            index
                                                                        ]
                                                                            ?.TaxRatePercent !==
                                                                        null
                                                                        ? Number(
                                                                            lineItems?.[
                                                                                index
                                                                            ]
                                                                                ?.TaxRatePercent,
                                                                        )
                                                                        : 0
                                                                const taxable =
                                                                    Math.max(
                                                                        0,
                                                                        quantity *
                                                                        unitPrice -
                                                                        disc,
                                                                    )
                                                                const lineTax =
                                                                    (taxable *
                                                                        lineTaxRate) /
                                                                    100
                                                                const amountWithTax =
                                                                    taxable +
                                                                    lineTax
                                                                return `${amountWithTax.toFixed(
                                                                    2,
                                                                )}`
                                                            })()}
                                                        </td>

                                                        <td className="py-3 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    remove(index)
                                                                }
                                                                className="text-red-500 hover:text-red-700"
                                                            >
                                                                <Trash size={24} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </DesktopMode>

                                    {/* Mobile View: Stacked Card Items */}
                                    <MobileMode>
                                        <div className="space-y-3 mt-3">
                                            {fields.map((item, index) => (
                                                <div
                                                    key={item.id}
                                                    className="p-3 border rounded-lg bg-gray-50 relative"
                                                >
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="font-semibold text-gray-700 text-sm">
                                                            Item #{index + 1}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                remove(index)
                                                            }
                                                            className="text-red-500 hover:text-red-700 p-1"
                                                        >
                                                            <Trash size={20} />
                                                        </button>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <FormTextFormField
                                                            control={control}
                                                            name={`Lines.${index}.Description`}
                                                            label="Description"
                                                            placeholder="Description"
                                                            errors={
                                                                errors?.Lines?.[
                                                                    index
                                                                ]?.Description
                                                            }
                                                        />
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.Quantity`}
                                                                label="Qty"
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]?.Quantity
                                                                }
                                                            />
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.UnitPrice`}
                                                                label="Unit Price"
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]?.UnitPrice
                                                                }
                                                            />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.DiscountAmount`}
                                                                label="Discount"
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]
                                                                        ?.DiscountAmount
                                                                }
                                                            />
                                                            <FormTextFormField
                                                                control={control}
                                                                name={`Lines.${index}.TaxRatePercent`}
                                                                label="Tax %"
                                                                type="number"
                                                                errors={
                                                                    errors?.Lines?.[
                                                                        index
                                                                    ]
                                                                        ?.TaxRatePercent
                                                                }
                                                            />
                                                        </div>
                                                        <div className="flex justify-between items-center pt-2 border-t text-sm font-semibold">
                                                            <span>Line Total:</span>
                                                            <span>
                                                                {(() => {
                                                                    const quantity =
                                                                        Number(
                                                                            lineItems?.[
                                                                                index
                                                                            ]
                                                                                ?.Quantity ||
                                                                            0,
                                                                        )
                                                                    const unitPrice =
                                                                        Number(
                                                                            lineItems?.[
                                                                                index
                                                                            ]
                                                                                ?.UnitPrice ||
                                                                            0,
                                                                        )
                                                                    const disc =
                                                                        Number(
                                                                            lineItems?.[
                                                                                index
                                                                            ]
                                                                                ?.DiscountAmount ||
                                                                            0,
                                                                        )
                                                                    const lineTaxRate =
                                                                        lineItems?.[
                                                                            index
                                                                        ]
                                                                            ?.TaxRatePercent !==
                                                                            undefined &&
                                                                            lineItems?.[
                                                                                index
                                                                            ]
                                                                                ?.TaxRatePercent !==
                                                                            null
                                                                            ? Number(
                                                                                lineItems?.[
                                                                                    index
                                                                                ]
                                                                                    ?.TaxRatePercent,
                                                                            )
                                                                            : 0
                                                                    const taxable =
                                                                        Math.max(
                                                                            0,
                                                                            quantity *
                                                                            unitPrice -
                                                                            disc,
                                                                        )
                                                                    const lineTax =
                                                                        (taxable *
                                                                            lineTaxRate) /
                                                                        100
                                                                    return (
                                                                        taxable + lineTax
                                                                    ).toFixed(2)
                                                                })()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </MobileMode>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const currentHeaderTax =
                                                getValues()?.TaxRatePercent
                                            const fallback =
                                                invoiceSetting &&
                                                    invoiceSetting.DefaultTaxRatePercent !==
                                                    undefined &&
                                                    invoiceSetting.DefaultTaxRatePercent !==
                                                    null
                                                    ? Number(
                                                        invoiceSetting.DefaultTaxRatePercent,
                                                    )
                                                    : 0
                                            append({
                                                Description: '',
                                                Quantity: 1,
                                                UnitPrice: 0,
                                                DiscountAmount: 0,
                                                TaxRatePercent:
                                                    currentHeaderTax !==
                                                        undefined &&
                                                        currentHeaderTax !== null &&
                                                        currentHeaderTax !== ''
                                                        ? Number(
                                                            currentHeaderTax,
                                                        )
                                                        : fallback,
                                            })
                                        }}
                                        className="mt-4 inline-flex items-center gap-2 text-blue-600 font-medium"
                                    >
                                        <Plus size={20} />
                                        Add Item
                                    </button>
                                </div>

                                {/* -------------------- SUMMARY -------------------- */}
                                <div className="mt-8 flex justify-end flex-col md:flex-row gap-6">
                                    <div className="w-full md:w-[50%] bg-gray-50 border rounded-lg p-4">
                                        <div className="flex justify-between mb-2 text-sm text-gray-600">
                                            <span>Subtotal</span>
                                            <span>{subtotal.toFixed(2)}</span>
                                        </div>

                                        <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
                                            {/* Default Tax Rate removed */}
                                        </div>

                                        <div className="flex justify-between mb-2 text-sm text-gray-600">
                                            <span>Discount</span>
                                            <span>
                                                {discountTotal.toFixed(2)}
                                            </span>
                                        </div>

                                        <div className="flex justify-between mb-2 text-sm text-gray-600">
                                            <span>Tax Amount</span>
                                            <span>{taxAmount.toFixed(2)}</span>
                                        </div>

                                        <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
                                            <span>Shipping</span>
                                            <div className="flex items-center gap-2">
                                                <FormTextFormField
                                                    control={control}
                                                    name="ShippingAmount"
                                                    type="number"
                                                    errors={
                                                        errors.ShippingAmount
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="flex justify-between text-lg font-semibold text-gray-900 border-t pt-3 mt-3">
                                            <span>Total</span>
                                            <span>{total.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormTextFormField
                                        control={control}
                                        name="NotesPublic"
                                        label="Public Notes"
                                        placeholder="Notes visible to customer..."
                                        errors={errors.NotesPublic}
                                        multiline
                                    />

                                    <FormTextFormField
                                        control={control}
                                        name="NotesInternal"
                                        label="Internal Notes"
                                        placeholder="Private notes..."
                                        errors={errors.NotesInternal}
                                        multiline
                                    />
                                </div>

                                {/* Terms and Conditions */}
                                <div className="space-y-2">
                                    <div className="font-bold text-gray-700">
                                        Terms & Conditions
                                    </div>
                                    <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200 whitespace-pre-line">
                                        {invoiceSetting?.TermsAndConditions
                                            ?.length > 0 ? (
                                            invoiceSetting.TermsAndConditions.map(
                                                (item, index) => (
                                                    <div
                                                        key={index}
                                                        className="mb-2 last:mb-0"
                                                    >
                                                        {item?.TermTitle && (
                                                            <span className="font-semibold text-gray-800">
                                                                {item.TermTitle}
                                                                :
                                                            </span>
                                                        )}
                                                        {item?.TermText && (
                                                            <span className="text-gray-700 ml-1">
                                                                {item.TermText}
                                                            </span>
                                                        )}
                                                    </div>
                                                ),
                                            )
                                        ) : (
                                            <div className="text-gray-500 italic">
                                                No terms and conditions
                                                configured
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* -------------------- ACTION BUTTONS (BOTTOM) -------------------- */}
                                <div className="pt-4 flex flex-col sm:flex-row gap-3 justify-end">
                                    <button
                                        type="button"
                                        onClick={handleSubmit(form =>
                                            onSubmit(form, 1),
                                        )}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg"
                                    >
                                        <FloppyDisk className="w-4 h-4" />
                                        Save as Draft
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit(form =>
                                            onSubmit(form, 5),
                                        )}
                                        className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg"
                                    >
                                        <FloppyDisk className="w-4 h-4" />
                                        Generate Invoice
                                    </button>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
            <InvoiceEmailModal
                open={openEmailModal}
                onClose={() => setOpenEmailModal(false)}
                onSubmit={handleSubmitInvoiceEmail}
                loading={sendingEmail}
            />
        </>
    )
}
